import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  DEFAULT_MINIMIZED_VIEW,
  MinimizedViewConfig,
} from "../../../cores/boards/constants/minimized-view-fields";

type MinimizeCommand = {
  token: number;
  config: MinimizedViewConfig;
} | null;

type ExpandCommand = {
  token: number;
} | null;

interface CrmBoardUiContextValue {
  globalMinimize: MinimizeCommand;
  globalExpand: ExpandCommand;
  requestGlobalMinimize: (config: MinimizedViewConfig) => void;
  requestGlobalExpand: () => void;
}

const CrmBoardUiContext = createContext<CrmBoardUiContextValue>({
  globalMinimize: null,
  globalExpand: null,
  requestGlobalMinimize: () => undefined,
  requestGlobalExpand: () => undefined,
});

export const useCrmBoardUi = () => useContext(CrmBoardUiContext);

export const CrmBoardUiProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [globalMinimize, setGlobalMinimize] = useState<MinimizeCommand>(null);
  const [globalExpand, setGlobalExpand] = useState<ExpandCommand>(null);

  const requestGlobalMinimize = useCallback((config: MinimizedViewConfig) => {
    setGlobalMinimize({
      token: Date.now(),
      config: { ...DEFAULT_MINIMIZED_VIEW, ...config, title: true },
    });
  }, []);

  const requestGlobalExpand = useCallback(() => {
    setGlobalExpand({ token: Date.now() });
  }, []);

  const value = useMemo(
    () => ({
      globalMinimize,
      globalExpand,
      requestGlobalMinimize,
      requestGlobalExpand,
    }),
    [globalMinimize, globalExpand, requestGlobalMinimize, requestGlobalExpand]
  );

  return (
    <CrmBoardUiContext.Provider value={value}>
      {children}
    </CrmBoardUiContext.Provider>
  );
};
